import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, AlertCircle, Clock, CheckCircle, XCircle,
  MessageSquare, Tag, Calendar, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ticketService } from '../../services/ticketService';
import { useDateFormat } from '../../hooks/useDateFormat';

const STATUS_CONFIG = {
  OPEN:        { labelKey: 'support.statusOpen',       icon: AlertCircle,  color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { labelKey: 'support.statusInProgress', icon: Clock,        color: 'bg-yellow-100 text-yellow-700' },
  RESOLVED:    { labelKey: 'support.statusResolved',   icon: CheckCircle,  color: 'bg-green-100 text-green-700' },
  CLOSED:      { labelKey: 'support.statusClosed',     icon: XCircle,      color: 'bg-gray-100 text-gray-700' },
};

const PRIORITY_CONFIG = {
  HIGH:   { labelKey: 'support.priorityHigh',   color: 'bg-red-100 text-red-700' },
  MEDIUM: { labelKey: 'support.priorityMedium', color: 'bg-yellow-100 text-yellow-700' },
  LOW:    { labelKey: 'support.priorityLow',    color: 'bg-green-100 text-green-700' },
};

const CATEGORY_OPTIONS = [
  { value: 'TECHNICAL',       labelKey: 'support.categoryTechnical' },
  { value: 'BILLING',         labelKey: 'support.categoryBilling' },
  { value: 'FEATURE_REQUEST', labelKey: 'support.categoryFeatureRequest' },
  { value: 'QUESTION',        labelKey: 'support.categoryQuestion' },
  { value: 'OTHER',           labelKey: 'support.categoryOther' },
];

const Support = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const availableServices = [];
  if (localStorage.getItem('hasCmmsSubscription') === 'true') {
    availableServices.push({ value: 'CMMS', label: t('support.serviceCmms') });
  }
  if (localStorage.getItem('hasChatbotSubscription') === 'true') {
    availableServices.push({ value: 'CHATBOT', label: t('support.serviceChatbot') });
  }

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    category: 'TECHNICAL',
    serviceType: availableServices.length > 0 ? availableServices[0].value : '',
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const search = searchTerm.trim() || undefined;
      const data = await ticketService.getTickets(statusFilter, search, currentPage, 10);
      setTickets(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, currentPage]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await ticketService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await ticketService.createTicket(newTicket);
      setShowCreateModal(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'MEDIUM',
        category: 'TECHNICAL',
        serviceType: availableServices.length > 0 ? availableServices[0].value : '',
      });
      fetchTickets();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.OPEN;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {t(config.labelKey)}
      </span>
    );
  };

  const PriorityBadge = ({ priority }) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.MEDIUM;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {t(config.labelKey)}
      </span>
    );
  };

  const statCards = [
    { labelKey: 'support.statusOpen',       value: stats.open,       color: 'text-blue-600',   bg: 'bg-blue-50',   icon: AlertCircle },
    { labelKey: 'support.statusInProgress', value: stats.inProgress, color: 'text-yellow-600', bg: 'bg-yellow-50', icon: Clock },
    { labelKey: 'support.statusResolved',   value: stats.resolved,   color: 'text-green-600',  bg: 'bg-green-50',  icon: CheckCircle },
    { labelKey: 'support.statusClosed',     value: stats.closed,     color: 'text-gray-600',   bg: 'bg-gray-50',   icon: XCircle },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('nav.support')}</h1>
          <p className="text-gray-600 mt-1">{t('support.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          {t('support.newTicket')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.labelKey} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600">{t(stat.labelKey)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('support.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">{t('support.allStatus')}</option>
            <option value="OPEN">{t('support.statusOpen')}</option>
            <option value="IN_PROGRESS">{t('support.statusInProgress')}</option>
            <option value="RESOLVED">{t('support.statusResolved')}</option>
            <option value="CLOSED">{t('support.statusClosed')}</option>
          </select>
        </div>
      </div>

      {/* Ticket List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{t('support.noTicketsFound')}</h3>
            <p className="text-gray-600">{t('support.noTicketsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('support.ticket')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.priority')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('support.serviceLabel')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('support.categoryLabel')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('support.replies')}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.created')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/portal/support/${ticket.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-xs text-gray-500 font-mono">{ticket.ticketNumber}</span>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{ticket.title}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td className="px-6 py-4">
                        <PriorityBadge priority={ticket.priority} />
                      </td>
                      <td className="px-6 py-4">
                        {ticket.serviceType && (
                          <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {ticket.serviceType}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                          <Tag className="w-3.5 h-3.5" />
                          {ticket.category
                            ? t(`support.category${ticket.category.charAt(0) + ticket.category.slice(1).toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`, { defaultValue: ticket.category.replace('_', ' ') })
                            : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                          <MessageSquare className="w-4 h-4" />
                          {ticket.replyCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3.5 h-3.5" />
                          {ticket.createdAt ? formatDate(ticket.createdAt) : ''}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {t('support.showingTo', {
                    from: currentPage * 10 + 1,
                    to: Math.min((currentPage + 1) * 10, totalElements),
                    total: totalElements,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    {t('support.pageOf', { current: currentPage + 1, total: totalPages })}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">{t('support.createTicket')}</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.titleLabel')} *</label>
                <input
                  type="text"
                  required
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('support.titlePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  rows={4}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder={t('support.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.serviceLabel')} *</label>
                  <select
                    required
                    value={newTicket.serviceType}
                    onChange={(e) => setNewTicket({ ...newTicket, serviceType: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('support.selectService')}</option>
                    {availableServices.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                    <option value="GENERAL">{t('support.generalOther')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.categoryLabel')}</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.priorityLabel')}</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="LOW">{t('support.priorityLow')}</option>
                  <option value="MEDIUM">{t('support.priorityMedium')}</option>
                  <option value="HIGH">{t('support.priorityHigh')}</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {creating ? t('support.creating') : t('support.createTicketBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
